// @oasis-permission-inherits: /capcode (capcode:view)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  BookOpen,
  Braces,
  ChevronLeft,
  ChevronRight,
  FileCode2,
  Folder,
  GitBranch,
  Hash,
  KeyRound,
  RefreshCcw,
  Search,
  Tags,
  Users,
} from "lucide-react";
import * as React from "react";
import { Streamdown } from "streamdown";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import {
  fetchRunbookContent,
  fetchRunbooks,
  type RunbookContentResponse,
  type RunbookFile,
} from "~/modules/cap-code/network";

export const Route = createFileRoute("/_dash/capcode/runbooks")({
  validateSearch: z.object({
    runbook: z.string().optional(),
  }),
  component: RunbooksRoute,
});

const READ_LIMIT = 16_000;
const SUPPORTED_REPO_OWNERS = new Set(["bungeeapp", "bungee-app"]);

type RunbookTreeNode = {
  children: Map<string, RunbookTreeNode>;
  name: string;
  path: string;
  runbook: RunbookFile | null;
};

type RunbookViewMode = "raw" | "preview";

function runbookMatchesQuery(runbook: RunbookFile, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return [
    runbook.repo,
    runbook.sourcePath,
    runbook.service ?? "",
    runbook.title ?? "",
    runbook.summary ?? "",
    ...runbook.owners,
    ...runbook.keywords,
    ...Object.entries(runbook.labels).flat(),
  ]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

function RunbooksRoute() {
  const { runbook } = Route.useSearch();
  const selectedKey = runbook ?? null;
  const navigate = useNavigate({ from: Route.fullPath });
  const [runbooks, setRunbooks] = React.useState<RunbookFile[]>([]);
  const [content, setContent] = React.useState<RunbookContentResponse | null>(null);
  const [query, setQuery] = React.useState("");
  const [readOffset, setReadOffset] = React.useState(0);
  const [viewMode, setViewMode] = React.useState<RunbookViewMode>("preview");
  const [loading, setLoading] = React.useState(true);
  const [contentLoading, setContentLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadRunbooks = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nextRunbooks = await fetchRunbooks({ repo: "", service: "", directory: "" });
      setRunbooks(nextRunbooks);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load runbooks");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadRunbooks();
  }, [loadRunbooks]);

  React.useEffect(() => {
    if (runbooks.length === 0) {
      return;
    }
    if (selectedKey && runbooks.some((runbook) => runbook.r2Key === selectedKey)) {
      return;
    }
    const firstRunbook = runbooks[0];
    if (!firstRunbook) {
      return;
    }
    navigate({
      search: (previous) => ({ ...previous, runbook: firstRunbook.r2Key }),
      replace: true,
    });
  }, [navigate, runbooks, selectedKey]);

  React.useEffect(() => {
    setReadOffset(0);
  }, [selectedKey]);

  React.useEffect(() => {
    if (!selectedKey) {
      setContent(null);
      return;
    }
    let cancelled = false;
    setContentLoading(true);
    setError(null);
    fetchRunbookContent({ r2Key: selectedKey, offset: readOffset, limit: READ_LIMIT })
      .then((nextContent) => {
        if (!cancelled) {
          setContent(nextContent);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to read runbook");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setContentLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [readOffset, selectedKey]);

  const filteredRunbooks = React.useMemo(
    () => runbooks.filter((runbook) => runbookMatchesQuery(runbook, query)),
    [query, runbooks]
  );
  const selectedRunbook =
    filteredRunbooks.find((runbook) => runbook.r2Key === selectedKey) ??
    runbooks.find((runbook) => runbook.r2Key === selectedKey) ??
    null;

  function selectRunbook(runbook: RunbookFile) {
    navigate({
      search: (previous) => ({ ...previous, runbook: runbook.r2Key }),
    });
  }

  return (
    <div className="-m-4 flex h-[calc(100%+2rem)] flex-col overflow-hidden bg-white text-zinc-950">
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-zinc-200 bg-white px-6 py-5">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight">Runbooks</h1>
            <p className="mt-1 text-sm text-zinc-600">
              Mirrored repo RUNBOOK.md guidance from main branches.
            </p>
          </div>
          <Button variant="outline" onClick={() => void loadRunbooks()} disabled={loading}>
            <RefreshCcw size={16} />
            Refresh
          </Button>
        </header>

        {error && (
          <div className="mx-6 mt-4 shrink-0 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden bg-white min-[520px]:grid-cols-[240px_minmax(0,1fr)] md:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)]">
          <aside className="flex min-h-0 flex-col border-b border-zinc-200 bg-white min-[520px]:border-b-0 min-[520px]:border-r">
            <RunbookSidebarHeader
              count={filteredRunbooks.length}
              loading={loading}
              query={query}
              onQueryChange={setQuery}
            />
            <div className="min-h-[220px] flex-1 overflow-hidden min-[520px]:min-h-0">
              {filteredRunbooks.length === 0 ? (
                <EmptyRunbooks loading={loading} />
              ) : (
                <RunbookTree
                  runbooks={filteredRunbooks}
                  selectedKey={selectedKey}
                  onSelect={selectRunbook}
                />
              )}
            </div>
          </aside>

          <section className="flex min-h-0 flex-col bg-white">
            {selectedRunbook ? (
              <RunbookDetail
                runbook={selectedRunbook}
                content={content}
                loading={contentLoading}
                offset={readOffset}
                onOffsetChange={setReadOffset}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            ) : (
              <div className="flex min-h-full items-center justify-center p-8 text-center text-sm text-zinc-500">
                <div>
                  <BookOpen className="mx-auto mb-3" size={28} />
                  Select a runbook to read it.
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function RunbookSidebarHeader({
  count,
  loading,
  query,
  onQueryChange,
}: {
  count: number;
  loading: boolean;
  query: string;
  onQueryChange: (value: string) => void;
}) {
  return (
    <div className="shrink-0 border-b border-zinc-200">
      <div className="flex h-11 items-center gap-3 px-4">
        <div className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-900">Files</div>
        <Badge className="border-zinc-200 bg-zinc-50 text-zinc-600">{count}</Badge>
        {loading && <Badge className="border-sky-200 bg-sky-50 text-sky-700">loading</Badge>}
      </div>
      <div className="space-y-2 border-t border-zinc-100 p-3">
        <FilterInput
          icon={<Search size={15} />}
          label="Filter"
          value={query}
          placeholder="title, owner, keyword"
          onChange={onQueryChange}
        />
      </div>
    </div>
  );
}

function FilterInput({
  icon,
  label,
  value,
  placeholder,
  onChange,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex h-10 items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900">
      {icon}
      <span className="shrink-0 text-xs font-medium text-zinc-500">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-400"
      />
    </label>
  );
}

function RunbookTree({
  runbooks,
  selectedKey,
  onSelect,
}: {
  runbooks: RunbookFile[];
  selectedKey: string | null;
  onSelect: (runbook: RunbookFile) => void;
}) {
  const tree = React.useMemo(() => buildRunbookTree(runbooks), [runbooks]);
  const selectedTreePath = React.useMemo(() => {
    const selectedRunbook = runbooks.find((runbook) => runbook.r2Key === selectedKey);
    return selectedRunbook ? treePathForRunbook(selectedRunbook) : null;
  }, [runbooks, selectedKey]);

  return (
    <div className="h-full overflow-auto py-2">
      {sortedTreeNodes(tree.children).map((node) => (
        <RunbookTreeNodeRow
          key={node.path}
          node={node}
          level={0}
          selectedTreePath={selectedTreePath}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function buildRunbookTree(runbooks: RunbookFile[]): RunbookTreeNode {
  const root: RunbookTreeNode = { children: new Map(), name: "", path: "", runbook: null };
  for (const runbook of runbooks) {
    const parts = treePathForRunbook(runbook).split("/").filter(Boolean);
    let currentNode = root;
    let currentPath = "";
    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      let nextNode = currentNode.children.get(part);
      if (!nextNode) {
        nextNode = { children: new Map(), name: part, path: currentPath, runbook: null };
        currentNode.children.set(part, nextNode);
      }
      currentNode = nextNode;
    }
    currentNode.runbook = runbook;
  }
  return root;
}

function sortedTreeNodes(children: Map<string, RunbookTreeNode>): RunbookTreeNode[] {
  return [...children.values()].sort((left, right) => {
    const leftIsDirectory = left.children.size > 0 && !left.runbook;
    const rightIsDirectory = right.children.size > 0 && !right.runbook;
    if (leftIsDirectory !== rightIsDirectory) {
      return leftIsDirectory ? -1 : 1;
    }
    return left.name.localeCompare(right.name);
  });
}

function RunbookTreeNodeRow({
  node,
  level,
  selectedTreePath,
  onSelect,
}: {
  node: RunbookTreeNode;
  level: number;
  selectedTreePath: string | null;
  onSelect: (runbook: RunbookFile) => void;
}) {
  const isSelected = node.path === selectedTreePath;
  const hasChildren = node.children.size > 0;
  const paddingLeft = 10 + level * 16;

  if (node.runbook) {
    const runbook = node.runbook;
    return (
      <>
        <button
          type="button"
          className={`flex min-h-8 w-full items-center gap-2 rounded-none py-1.5 pr-3 text-left text-sm ${
            isSelected
              ? "bg-blue-50 font-medium text-zinc-950 shadow-[inset_2px_0_0_#2563eb]"
              : "text-zinc-700 hover:bg-zinc-50"
          }`}
          style={{ paddingLeft }}
          onClick={() => onSelect(runbook)}
        >
          <FileCode2 size={15} className="shrink-0 text-teal-700" />
          <span className="min-w-0 flex-1 truncate">{node.name}</span>
        </button>
        {sortedTreeNodes(node.children).map((child) => (
          <RunbookTreeNodeRow
            key={child.path}
            node={child}
            level={level + 1}
            selectedTreePath={selectedTreePath}
            onSelect={onSelect}
          />
        ))}
      </>
    );
  }

  return (
    <>
      <div
        className="flex min-h-8 items-center gap-2 py-1.5 pr-3 text-sm font-medium text-zinc-600"
        style={{ paddingLeft }}
      >
        {hasChildren ? (
          <ChevronRight size={14} className="shrink-0 rotate-90 text-zinc-400" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <Folder size={15} className="shrink-0 text-zinc-500" />
        <span className="min-w-0 flex-1 truncate">{node.name}</span>
      </div>
      {sortedTreeNodes(node.children).map((child) => (
        <RunbookTreeNodeRow
          key={child.path}
          node={child}
          level={level + 1}
          selectedTreePath={selectedTreePath}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

function treePathForRunbook(runbook: RunbookFile): string {
  const sourcePath = runbook.sourcePath.replace(/^\/+/, "");
  return `${repoNameForTree(runbook.repo)}/${sourcePath}`;
}

function repoNameForTree(repo: string): string {
  const [owner, ...nameParts] = repo.split("/");
  if (owner && SUPPORTED_REPO_OWNERS.has(owner) && nameParts.length > 0) {
    return nameParts.join("/");
  }
  return repo;
}

function sourceFileName(sourcePath: string): string {
  const parts = sourcePath.split("/").filter(Boolean);
  const fileName = parts[parts.length - 1];
  return fileName ?? "RUNBOOK.md";
}

function RunbookDetail({
  runbook,
  content,
  loading,
  offset,
  onOffsetChange,
  viewMode,
  onViewModeChange,
}: {
  runbook: RunbookFile;
  content: RunbookContentResponse | null;
  loading: boolean;
  offset: number;
  onOffsetChange: (offset: number) => void;
  viewMode: RunbookViewMode;
  onViewModeChange: (mode: RunbookViewMode) => void;
}) {
  const activeContent = content?.runbook.r2Key === runbook.r2Key ? content : null;
  const end = activeContent ? activeContent.offset + activeContent.content.length : offset;
  const hasContent = Boolean(activeContent && activeContent.totalChars > 0);
  const canPageBack = offset > 0;
  const canPageForward = Boolean(activeContent?.truncated);
  const labelItems = React.useMemo(
    () => Object.entries(runbook.labels).map(([key, value]) => `${key}=${value}`),
    [runbook.labels]
  );

  let contentBody: React.ReactNode = (
    <div className="flex h-full items-center justify-center p-8 text-sm text-zinc-600">
      No content loaded
    </div>
  );
  if (activeContent) {
    contentBody = <RunbookContentEmpty runbook={runbook} />;
  }
  if (activeContent && hasContent) {
    contentBody =
      viewMode === "preview" ? (
        <RunbookMarkdownPreview content={activeContent.content} />
      ) : (
        <RunbookCodeBlock content={activeContent.content} />
      );
  }

  return (
    <>
      <div className="shrink-0 border-b border-zinc-200 bg-white">
        <div className="space-y-3 p-4">
          <div className="flex min-w-0 flex-col gap-2 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="mb-1 flex min-w-0 items-center gap-2 text-xs font-medium text-zinc-500">
                <FileCode2 size={14} className="shrink-0" />
                <span className="truncate">{sourceFileName(runbook.sourcePath)}</span>
              </div>
              <h2 className="break-words text-lg font-semibold text-zinc-950">
                {runbook.title ?? runbook.sourcePath}
              </h2>
              <div className="mt-1 flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                <SourceFact icon={<GitBranch size={13} />} value={runbook.repo} />
                <SourceFact icon={<FileCode2 size={13} />} value={runbook.sourcePath} />
                <SourceFact icon={<KeyRound size={13} />} value={formatBytes(runbook.sizeBytes)} />
                {runbook.sourceSha && (
                  <SourceFact icon={<Hash size={13} />} value={runbook.sourceSha.slice(0, 8)} />
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-xs text-zinc-500">
                indexed {formatTimestamp(runbook.lastIndexedAt)}
              </span>
              <Badge className={statusBadgeClass(runbook.status)}>{runbook.status}</Badge>
            </div>
          </div>
          {runbook.summary && <p className="max-w-4xl text-sm text-zinc-600">{runbook.summary}</p>}
          <div className="grid gap-3 md:grid-cols-3">
            <MetaGroup icon={<Users size={14} />} label="Owners" items={runbook.owners} />
            <MetaGroup icon={<Tags size={14} />} label="Keywords" items={runbook.keywords} />
            <MetaGroup icon={<Braces size={14} />} label="Labels" items={labelItems} />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex h-11 shrink-0 items-center justify-between border-b border-zinc-200 bg-zinc-50 px-4 text-xs text-zinc-500">
          <span>{contentStatus(activeContent, loading, end)}</span>
          <div className="flex items-center gap-2">
            <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
            <Button
              variant="outline"
              className="h-8 px-3 text-xs"
              disabled={!canPageBack}
              onClick={() => onOffsetChange(Math.max(0, offset - READ_LIMIT))}
            >
              <ChevronLeft size={14} />
              Previous
            </Button>
            <Button
              variant="outline"
              className="h-8 px-3 text-xs"
              disabled={!canPageForward}
              onClick={() => onOffsetChange(offset + READ_LIMIT)}
            >
              Next
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
        <div className="relative min-h-0 flex-1 overflow-auto bg-white">
          {loading && (
            <div className="absolute inset-x-0 top-0 z-10 border-b border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-700">
              Loading content
            </div>
          )}
          {contentBody}
        </div>
      </div>
    </>
  );
}

function RunbookContentEmpty({ runbook }: { runbook: RunbookFile }) {
  return (
    <div className="flex h-full items-center justify-center p-8 text-center text-sm text-zinc-600">
      <div className="max-w-sm">
        <BookOpen className="mx-auto mb-3 text-zinc-400" size={28} />
        <div className="font-medium text-zinc-900">Content has not been mirrored yet</div>
        <p className="mt-1 leading-5">
          The runbook metadata exists for {runbook.sourcePath}, but the stored content is empty.
          Refresh after the next runbook sync.
        </p>
      </div>
    </div>
  );
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: RunbookViewMode;
  onChange: (mode: RunbookViewMode) => void;
}) {
  const options: { id: RunbookViewMode; label: string }[] = [
    { id: "preview", label: "Preview" },
    { id: "raw", label: "Raw" },
  ];
  return (
    <div className="inline-flex h-8 items-center rounded-md border border-zinc-200 bg-white p-0.5">
      {options.map((option) => {
        const active = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            aria-pressed={active}
            className={cn(
              "h-7 rounded-sm px-2.5 text-xs font-medium transition-colors",
              active
                ? "bg-zinc-900 text-white"
                : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function stripFrontmatter(content: string): string {
  if (!content.startsWith("---\n")) {
    return content;
  }
  const end = content.indexOf("\n---", 4);
  if (end === -1) {
    return content;
  }
  return content.slice(end + 4).replace(/^\n+/, "");
}

function RunbookMarkdownPreview({ content }: { content: string }) {
  const body = React.useMemo(() => stripFrontmatter(content), [content]);
  return (
    <div className="min-h-full bg-white px-6 py-5">
      <Streamdown className="prose prose-sm max-w-none break-words text-sm text-zinc-900">
        {body}
      </Streamdown>
    </div>
  );
}

function RunbookCodeBlock({ content }: { content: string }) {
  const lines = React.useMemo(() => content.split("\n"), [content]);

  return (
    <div className="min-h-full bg-white py-3 font-mono text-xs leading-5 text-zinc-900">
      {lines.map((line, index) => (
        <div
          key={`${index}-${line}`}
          className="grid min-h-5 grid-cols-[56px_minmax(0,1fr)] hover:bg-zinc-50"
        >
          <div className="select-none border-r border-zinc-100 pr-3 text-right text-zinc-400">
            {index + 1}
          </div>
          <pre className="min-w-0 whitespace-pre-wrap break-words px-4">{line || " "}</pre>
        </div>
      ))}
    </div>
  );
}

function MetaGroup({
  icon,
  label,
  items,
}: {
  icon: React.ReactNode;
  label: string;
  items: string[];
}) {
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
        {icon}
        {label}
      </div>
      <div className="flex min-h-7 flex-wrap items-start gap-1.5">
        {items.length > 0 ? (
          items.map((item) => (
            <Badge key={item} className="border-zinc-200 bg-zinc-50 text-zinc-700">
              {item}
            </Badge>
          ))
        ) : (
          <span className="text-xs text-zinc-400">none</span>
        )}
      </div>
    </div>
  );
}

function SourceFact({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      {icon}
      <span className="truncate">{value}</span>
    </span>
  );
}

function EmptyRunbooks({ loading }: { loading: boolean }) {
  return (
    <div className="flex h-full min-h-[240px] items-center justify-center p-8 text-center text-sm text-zinc-500">
      <div>
        <BookOpen className="mx-auto mb-3" size={28} />
        {loading ? "Loading runbooks..." : "No mirrored runbooks match these filters."}
      </div>
    </div>
  );
}

function contentStatus(
  content: RunbookContentResponse | null,
  loading: boolean,
  end: number
): string {
  if (content) {
    if (content.totalChars === 0) {
      return "No content mirrored yet";
    }
    return `Showing ${content.offset + 1}-${end} of ${content.totalChars} chars`;
  }
  if (loading) {
    return "Loading content";
  }
  return "No content loaded";
}

function statusBadgeClass(status: string): string {
  if (status === "active") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (status === "deleted" || status === "deprecated") {
    return "border-red-200 bg-red-50 text-red-700";
  }
  return "border-zinc-200 bg-zinc-50 text-zinc-700";
}

function formatBytes(sizeBytes: number): string {
  if (sizeBytes < 1_024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1_048_576) {
    return `${Math.round(sizeBytes / 1_024)} KB`;
  }
  return `${(sizeBytes / 1_048_576).toFixed(1)} MB`;
}

function formatTimestamp(timestamp: number): string {
  if (!timestamp) {
    return "unknown";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}
